import { doctorBffApi, InvalidateArr } from '@/modules';
import { wrapError } from '../error';
import { medicalHoApi } from '@/modules/surgicalEvent/ho/medicalHo.slice';
import type { RootState } from '@/store';
import {
  MasterClearPayload,
  PersistSuggestionPayload,
  ScratchMhoSuggestionService
} from './scratchMhoSuggestion.service';

export const scratchSuggestionApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    persistSuggestion: builder.mutation<void, PersistSuggestionPayload>({
      queryFn: (payload, { getState }) =>
        ScratchMhoSuggestionService.getInstance()
          .persistSuggestion(
            payload,
            medicalHoApi.endpoints.medicalHo.select({
              patientId: payload.activeSe.patient.id
            })(getState() as RootState).data
          )
          .then(() => ({ data: {} as any }))
          .catch(wrapError),
      invalidatesTags: (result, error, payload) => {
        const patientId = payload.activeSe.patient.id;
        const arr: InvalidateArr = ['Dashboard'];

        const isDrawer = payload.suggestion.suggestionType === 'drawer';
        const isClear = payload.suggestion.suggestionType === 'clear';

        if (isDrawer || isClear || hasMho(payload)) {
          arr.push(
            { type: 'MedicalHo', id: patientId },
            { type: 'SurgeryInfo', id: patientId } // for ASA prediction update
          );
        }

        if (!isDrawer && hasAllergies(payload)) {
          arr.push({ type: 'Allergy', id: patientId });
        }

        return arr;
      }
    }),

    masterClear: builder.mutation<void, MasterClearPayload>({
      queryFn: (payload, { getState }) =>
        ScratchMhoSuggestionService.getInstance()
          .masterClear({
            ...payload,
            medicalHo: payload.activeSe
              ? medicalHoApi.endpoints.medicalHo.select({
                  patientId: payload.activeSe.patient.id
                })(getState() as RootState).data
              : undefined
          })
          .then(() => ({ data: {} as any }))
          .catch(wrapError),
      invalidatesTags: (result, error, payload) => {
        const patientId = payload.activeSe?.patient.id;

        return [
          'Dashboard',
          { type: 'MedicalHo', id: patientId },
          { type: 'SurgeryInfo', id: patientId }, // for ASA prediction update
          { type: 'Allergy', id: patientId }
        ];
      }
    })
  }),
  overrideExisting: false
});

function hasAllergies(payload: PersistSuggestionPayload) {
  return (
    payload.category === 'ALLERGIES' ||
    getReqBody(payload).some((data) => data.category === 'ALLERGIES')
  );
}

function hasMho(payload: PersistSuggestionPayload) {
  return (
    payload.category !== 'ALLERGIES' ||
    getReqBody(payload).some((data) => data.category !== 'ALLERGIES')
  );
}

function getReqBody(payload: PersistSuggestionPayload) {
  const s = payload.suggestion;
  if (s.suggestionType === 'bi-choice') {
    return s.toRequestBody(payload.biChoiceChoice);
  }
  if (s.suggestionType === 'clear') {
    return s.toRequestBody(payload.activeSe);
  }

  return s.toRequestBody();
}

export const { usePersistSuggestionMutation, useMasterClearMutation } =
  scratchSuggestionApi;
